"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { CheckCircle, Search, Zap, Shield, Cpu, Network, Gauge } from "lucide-react";

type IconName = "search" | "zap" | "shield" | "cpu" | "network" | "gauge";

const iconMap: Record<IconName, React.ComponentType<{ className?: string }>> = {
  cpu: Cpu,
  gauge: Gauge,
  network: Network,
  search: Search,
  shield: Shield,
  zap: Zap,
};

export type FeatureCardContent = {
  icon: IconName;
  title: string;
  description: string;
  list?: string[];
  gridStats?: string[];
  button?: { text: string; link?: string; variant?: "default" | "outline" };
};

export function FeatureCard({ content, idx }: { content: FeatureCardContent; idx: number }) {
  const Icon = iconMap[content.icon];

  // Enable hover animations only on lg+ to avoid md layout shifts
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsLargeScreen(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const ButtonEl = (
    <Button variant={content.button?.variant || "default"} className="w-full">
      {content.button?.text}
    </Button>
  );

  return (
    <motion.div 
        className="h-full"
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.1 * (idx + 1), ease: "easeOut" }}
        whileHover={isLargeScreen ? { scale: 1.02, x: 10 } : undefined}
    >
      <Card className="h-full border-0 shadow-lg bg-white/80 backdrop-blur-sm hover:shadow-xl transition-all duration-300 p-0">
        <CardContent className="p-6 text-center flex flex-col justify-between h-full">
          <div>
            <motion.div
              className="bg-primary size-12 md:size-16 rounded-xl flex items-center justify-center mx-auto mb-6 shadow-lg"
              whileHover={isLargeScreen ? { scale: 1.1, rotate: 5 } : undefined}
              transition={{ duration: 0.2 }}
            >
              <Icon className="size-6 md:size-8 text-white" />
            </motion.div>

            <h3 className="text-xl md:text-2xl font-bold mb-2 text-gray-900">{content.title}</h3>
            <p className="text-sm md:text-base text-muted-foreground mb-6">{content.description}</p>

            {content.list && (
              <div className="space-y-3 mb-6 text-left">
                {content.list.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <CheckCircle className="size-4 md:size-5 text-primary mt-1 flex-shrink-0" />
                    <p className="text-sm md:text-base text-muted-foreground">{item}</p>
                  </div>
                ))}
              </div>
            )}

            {content.gridStats && (
              <div className="grid grid-cols-3 gap-3 mb-6">
                {content.gridStats.map((label, idx) => (
                  <div key={idx} className="p-3 bg-white rounded-lg shadow-sm">
                    <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                    <div className="text-xs font-medium">{label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {content.button?.link ? (
            <Link href={content.button.link} target="_blank" className="w-full">
              {ButtonEl}
            </Link>
          ) : (
            ButtonEl
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

